import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Send, 
  Calendar, 
  Clock,
  CheckCircle,
  Facebook,
  Twitter,
  Linkedin,
  AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business: '',
    service: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');

  const services = [
    'AI Chatbot Implementation',
    'Email Automation',
    'Social Media Automation',
    'SEO Optimization',
    'Local SEO',
    'Video Production',
    'Blog Writing',
    'Reputation Management',
    'GMB Management',
    'Complete Marketing Audit'
  ];

  const contactMethods = [
    {
      icon: Phone,
      title: 'Call Us',
      detail: '(213) 375-4006',
      description: 'Speak directly with our team',
      action: 'tel:+12133754006'
    },
    {
      icon: Mail,
      title: 'Email Us',
      detail: 'contact@localfinder.biz',
      description: 'Get a response within 24 hours',
      action: 'mailto:contact@localfinder.biz'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      detail: '12 S Raymond Ave',
      description: 'Pasadena, CA 91105',
      action: 'https://maps.google.com/?q=12+S+Raymond+Ave,+Pasadena,+CA+91105'
    }
  ];

  const socialLinks = [
    {
      icon: Facebook,
      url: 'https://www.facebook.com/localfinderbiz/',
      name: 'Facebook'
    },
    {
      icon: Twitter,
      url: 'https://twitter.com/LocalfinderBiz/',
      name: 'Twitter'
    },
    {
      icon: Linkedin,
      url: 'https://www.linkedin.com/company/local-finder-llc/',
      name: 'LinkedIn'
    }
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    setSubmitMessage('');
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setIsSubmitted(true);
        setSubmitMessage(response.data.message);
        
        // Reset form after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          setSubmitMessage('');
          setFormData({
            name: '',
            email: '',
            phone: '',
            business: '',
            service: '',
            message: ''
          });
        }, 5000);
      }
    } catch (error) {
      console.error('Error sending form:', error);
      setSubmitError(
        error.response?.data?.detail || 
        'There was an error sending your message. Please try calling us directly at (213) 375-4006.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-padding gradient-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6"
          >
            <Calendar className="text-accent-400" size={16} />
            <span className="text-white text-sm font-medium">Get Your Free Consultation</span>
          </motion.div>
          
          <h2 className="text-4xl lg:text-5xl font-black text-white mb-6">
            Ready to Transform
            <span className="text-gradient block">Your Business?</span>
          </h2>
          
          <p className="text-xl text-neutral-300 max-w-3xl mx-auto">
            Let's discuss how Local Finder LLC can help you implement AI automation 
            and proven marketing strategies to accelerate your business growth.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass rounded-3xl p-8"
          >
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-white mb-4">Get Your Free AI Audit</h3>
              <p className="text-neutral-300">
                Fill out the form below and we'll analyze your current setup and 
                provide personalized recommendations for AI automation opportunities.
              </p>
            </div>

            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:border-primary-400 transition-colors duration-300"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:border-primary-400 transition-colors duration-300"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:border-primary-400 transition-colors duration-300"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Business Name</label>
                    <input
                      type="text"
                      name="business"
                      value={formData.business}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:border-primary-400 transition-colors duration-300"
                      placeholder="Your business name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Service Interest</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-primary-400 transition-colors duration-300"
                  >
                    <option value="" className="bg-neutral-800">Select a service</option>
                    {services.map((service) => (
                      <option key={service} value={service} className="bg-neutral-800">
                        {service}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:border-primary-400 transition-colors duration-300 resize-none"
                    placeholder="Tell us about your business goals and how we can help..."
                  ></textarea>
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full btn-primary flex items-center justify-center space-x-2 ${
                    isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                  whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                  whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      <span>Send Message</span>
                    </>
                  )}
                </motion.button>

                {submitError && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 p-4 bg-red-500/20 border border-red-500/30 rounded-xl flex items-start space-x-3"
                  >
                    <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={18} />
                    <p className="text-red-300 text-sm">{submitError}</p>
                  </motion.div>
                )}
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12"
              >
                <CheckCircle className="text-green-400 w-16 h-16 mx-auto mb-4" />
                <h4 className="text-2xl font-bold text-white mb-4">Message Sent Successfully!</h4>
                <p className="text-neutral-300 mb-2">
                  {submitMessage || "Thank you for your interest. We'll get back to you within 24 hours with your personalized AI audit and recommendations."}
                </p>
                <div className="mt-4 p-3 bg-primary-500/20 border border-primary-500/30 rounded-lg">
                  <p className="text-primary-300 text-sm">
                    📧 Email sent to: localfinderbiz@gmail.com<br />
                    📞 Or call us directly: (213) 375-4006
                  </p>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            
            {/* Google Maps Embed */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass rounded-3xl p-2 overflow-hidden"
            >
              <div className="relative w-full h-80 rounded-2xl overflow-hidden">
                <iframe
                  src="https://maps.google.com/maps?cid=2198424706945818160&output=embed"
                  loading="lazy"
                  width="100%"
                  height="100%"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Local Finder LLC Location - 12 S Raymond Ave, Pasadena, CA 91105"
                  allowFullScreen
                  style={{ border: '0' }}
                  className="rounded-2xl"
                />
              </div>
              <div className="p-4 text-center">
                <h4 className="text-lg font-semibold text-white mb-2">Visit Our Pasadena Office</h4>
                <p className="text-neutral-300">12 S Raymond Ave, Pasadena, CA 91105</p>
              </div>
            </motion.div>

            {/* Contact Methods */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-white mb-6">Get in Touch</h3>
              {contactMethods.map((method, index) => (
                <motion.a
                  key={method.title}
                  href={method.action}
                  target={method.action.startsWith('http') ? '_blank' : '_self'}
                  rel={method.action.startsWith('http') ? 'noopener noreferrer' : ''}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  className="glass rounded-2xl p-6 flex items-start space-x-4 transition-all duration-300 hover:bg-white/20 block"
                >
                  <div className="w-12 h-12 bg-primary-500/20 backdrop-blur-sm border border-primary-500/30 rounded-xl flex items-center justify-center flex-shrink-0">
                    <method.icon className="text-primary-400" size={24} />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-1">{method.title}</h4>
                    <p className="text-primary-300 font-medium mb-1">{method.detail}</p>
                    <p className="text-neutral-400 text-sm">{method.description}</p>
                  </div>
                </motion.a>
              ))}
            </div>

            {/* Business Hours */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-6"
            >
              <div className="flex items-center space-x-3 mb-4">
                <Clock className="text-accent-400" size={24} />
                <h4 className="text-lg font-semibold text-white">Business Hours</h4>
              </div>
              <div className="space-y-2 text-neutral-300">
                <div className="flex justify-between">
                  <span>Monday - Friday</span>
                  <span>9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Saturday</span>
                  <span>10:00 AM - 4:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Sunday</span>
                  <span>Closed</span>
                </div>
              </div>
            </motion.div>

            {/* Social Media */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-6"
            >
              <h4 className="text-lg font-semibold text-white mb-4">Follow Us</h4>
              <div className="flex space-x-4">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <social.icon size={20} />
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;